<template>
    <form-wrapper class="intake-how-you-felt" @submit="handleFormSubmission(data)">
        <intake-form-section :title="$t('forms.intake.how_you_felt.sections.immediate_symptoms')">
            <p v-if="!data.immediateSymptoms.length" class="intake-symptoms__empty">
                {{ $t('forms.intake.how_you_felt.empty.immediate') }}
            </p>

            <div v-for="(symptom, index) in data.immediateSymptoms" :key="`immediate-${index}`" class="intake-symptoms__card">
                <div class="intake-symptoms__card-header">
                    <h4 class="intake-symptoms__card-title">
                        {{ $t('forms.intake.how_you_felt.symptom_label', { number: index + 1 }) }}
                    </h4>
                    <common-button
                        :label="$t('common.microcopy.remove')"
                        type="secondary"
                        size="small"
                        :isSubmittable="false"
                        @click="removeSymptom('immediateSymptoms', index)"
                    ></common-button>
                </div>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.how_you_felt.fields.symptom.label')"
                            :placeholder="$t('forms.intake.how_you_felt.fields.symptom.placeholder')"
                            :formData="data"
                            :valuePath="`immediateSymptoms.${index}.symptom`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell width="half">
                        <select-field
                            :label="$t('forms.intake.how_you_felt.fields.body_region.label')"
                            :placeholder="$t('forms.intake.how_you_felt.fields.body_region.placeholder')"
                            :options="bodyRegionOptions"
                            :formData="data"
                            :valuePath="`immediateSymptoms.${index}.bodyRegion`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                    <form-cell width="half">
                        <select-field
                            :label="$t('forms.intake.how_you_felt.fields.side.label')"
                            :placeholder="$t('forms.intake.how_you_felt.fields.side.placeholder')"
                            :options="sideOptions"
                            :formData="data"
                            :valuePath="`immediateSymptoms.${index}.side`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                </form-row>
            </div>

            <button class="intake-symptoms__add" type="button" @click="addImmediateSymptom">
                + {{ $t('forms.intake.how_you_felt.actions.add_symptom') }}
            </button>
        </intake-form-section>

        <intake-form-section :title="$t('forms.intake.how_you_felt.sections.delayed_symptoms')">
            <p v-if="!data.delayedSymptoms.length" class="intake-symptoms__empty">
                {{ $t('forms.intake.how_you_felt.empty.delayed') }}
            </p>

            <div v-for="(symptom, index) in data.delayedSymptoms" :key="`delayed-${index}`" class="intake-symptoms__card">
                <div class="intake-symptoms__card-header">
                    <h4 class="intake-symptoms__card-title">
                        {{ $t('forms.intake.how_you_felt.symptom_label', { number: index + 1 }) }}
                    </h4>
                    <common-button
                        :label="$t('common.microcopy.remove')"
                        type="secondary"
                        size="small"
                        :isSubmittable="false"
                        @click="removeSymptom('delayedSymptoms', index)"
                    ></common-button>
                </div>

                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.intake.how_you_felt.fields.symptom.label')"
                            :placeholder="$t('forms.intake.how_you_felt.fields.symptom.placeholder')"
                            :formData="data"
                            :valuePath="`delayedSymptoms.${index}.symptom`"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>

                <form-row>
                    <form-cell width="half">
                        <select-field
                            :label="$t('forms.intake.how_you_felt.fields.body_region.label')"
                            :placeholder="$t('forms.intake.how_you_felt.fields.body_region.placeholder')"
                            :options="bodyRegionOptions"
                            :formData="data"
                            :valuePath="`delayedSymptoms.${index}.bodyRegion`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                    <form-cell width="half">
                        <select-field
                            :label="$t('forms.intake.how_you_felt.fields.onset.label')"
                            :placeholder="$t('forms.intake.how_you_felt.fields.onset.placeholder')"
                            :options="onsetOptions"
                            :formData="data"
                            :valuePath="`delayedSymptoms.${index}.onset`"
                            @change="change($event)"
                        ></select-field>
                    </form-cell>
                </form-row>
            </div>

            <button class="intake-symptoms__add" type="button" @click="addDelayedSymptom">
                + {{ $t('forms.intake.how_you_felt.actions.add_symptom') }}
            </button>
        </intake-form-section>

        <intake-form-section :title="$t('forms.intake.how_you_felt.sections.emergency_response')">
            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.how_you_felt.fields.ems_called.label')"
                        :options="triStateOptions"
                        :formData="data"
                        valuePath="emsCalled"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <tabs-field
                        :label="$t('forms.intake.how_you_felt.fields.er_evaluated.label')"
                        :options="yesNoOptions"
                        :formData="data"
                        valuePath="erEvaluated"
                        size="small"
                        @change="change($event)"
                    ></tabs-field>
                </form-cell>
            </form-row>
        </intake-form-section>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import IntakeFormSection from 'components/forms/intake/IntakeFormSection'

    const BODY_REGIONS = [
        'head',
        'neck',
        'upper_back',
        'mid_back',
        'low_back',
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_elbow',
        'left_elbow',
        'right_wrist_hand',
        'left_wrist_hand',
        'chest',
        'abdomen',
        'right_hip',
        'left_hip',
        'right_thigh',
        'left_thigh',
        'right_knee',
        'left_knee',
        'right_leg',
        'left_leg',
        'right_ankle_foot',
        'left_ankle_foot',
        'other',
    ]

    export default {
        mixins: [FormMixin],

        components: {
            IntakeFormSection,
        },

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    immediateSymptoms: [],
                    delayedSymptoms: [],
                    emsCalled: undefined,
                    erEvaluated: undefined,
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            bodyRegionOptions() {
                return BODY_REGIONS.map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.body_region.options.${value}`),
                    value,
                }))
            },

            sideOptions() {
                return ['left', 'right', 'bilateral', 'central', 'not_applicable'].map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.side.options.${value}`),
                    value,
                }))
            },

            onsetOptions() {
                return ['minutes', 'hours', 'next_day', 'several_days', 'weeks', 'unsure'].map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.onset.options.${value}`),
                    value,
                }))
            },

            triStateOptions() {
                return ['yes', 'no', 'unsure'].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.tri_state.options.${value}`),
                    value,
                }))
            },

            yesNoOptions() {
                return ['yes', 'no'].map((value) => ({
                    label: this.$t(`forms.intake.what_happened.fields.tri_state.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            addImmediateSymptom() {
                this.change({
                    path: 'immediateSymptoms',
                    value: [...this.data.immediateSymptoms, { symptom: undefined, bodyRegion: undefined, side: undefined }],
                })
            },

            addDelayedSymptom() {
                this.change({
                    path: 'delayedSymptoms',
                    value: [...this.data.delayedSymptoms, { symptom: undefined, bodyRegion: undefined, onset: undefined }],
                })
            },

            removeSymptom(listPath, index) {
                const next = [...this.data[listPath]]
                next.splice(index, 1)
                this.change({ path: listPath, value: next })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-symptoms__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-symptoms__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: $color--bg-dropdown;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-symptoms__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-symptoms__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-symptoms__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
